/**
 * shared/lib/guardian-verification/canon-version.ts
 *
 * The canonical-form VERSION carried by every stored guardian answer digest.
 *
 * ── Why this exists (the defect it closes) ───────────────────────────────────
 *
 * A guardian answer is proven by digest equality: the ward's device stores
 * `hashSeedAnswer(canon(answer), salt)` at invite time, and months later the
 * guardian's device recomputes `hashSeedAnswer(canon(typedAnswer), salt)`. The
 * server sees only the two digests — by design (Rule 5, raw answers never reach
 * it) — so it CANNOT distinguish these two situations:
 *
 *   (a) the guardian typed the wrong answer, and
 *   (b) the two devices applied DIFFERENT `canon` functions to the right one.
 *
 * Both render as "That didn't match", and both burn one of the guardian's three
 * attempts. Case (b) is not hypothetical: the two devices are two independently
 * deployed client bundles separated in time by however long the invite has been
 * pending, and #2226 (2026-08-04) changed `canon` — widening it to absorb
 * punctuation, internal spacing, and Latin accents. Every invite created before
 * that change stored a digest of the NARROW form; every guardian answering
 * after it submits a digest of the WIDE one. Where the ward's answer contained
 * a character the widening now strips — a trailing full stop, an apostrophe, an
 * accent — a guardian who knows the answer perfectly is told they got it wrong,
 * three times, and then locked out. A ward on a stale cached bundle creating a
 * NEW invite produces the same skew in the other direction.
 *
 * CLAUDE.md ledger #53 named that cost and accepted it ("the invite must be
 * re-sent"). This module removes the cost instead: the version the ward hashed
 * under is RECORDED ON THE STORED ROW, served back with the questions, and used
 * verbatim by the guardian's device. Old invites keep matching under v1; new
 * ones use v2; a future v3 costs nobody an invite.
 *
 * ── Why this does NOT widen the guess budget ─────────────────────────────────
 *
 * Ledger #53 rejected a design that submitted the answer under BOTH forms and
 * let the server pass on either — correctly, because the server cannot tell
 * alternate ENCODINGS from alternate GUESSES, so N accepted digests is N
 * guesses per question. Nothing of that shape returns here. The guardian still
 * submits EXACTLY ONE digest per question, and the version that digest is
 * computed under is dictated by the stored row, not chosen by the submitter —
 * a guesser gains no second attempt, no second encoding, and no control over
 * which form is compared. The guess budget is byte-identical to before.
 *
 * ── Version log (append only; never renumber, never reuse) ───────────────────
 *
 *   v1 — trim + lowercase + NFKC. The whole canonicalization from the
 *        introduction of guardian answer hashing until 2026-08-04. Rows written
 *        before migration 0129 default to this, which is what they actually
 *        used.
 *   v2 — v1 plus: apostrophes removed, other punctuation/symbols → space,
 *        invisible format characters stripped, whitespace runs collapsed, Latin
 *        diacritics folded (ledger #53, PR #2226).
 *
 * A version is FROZEN the moment a row is written under it. Changing what an
 * existing version number means retroactively invalidates every digest stored
 * under it — the exact failure this module exists to prevent. To change the
 * canonical form, add the next integer and leave its predecessors untouched.
 */

/**
 * Every version this codebase can still hash under. Ordered, frozen, and the
 * single source of truth for the zod/DB bounds — a new version is added here
 * and the type/range checks follow automatically.
 */
export const GUARDIAN_ANSWER_CANON_VERSIONS = [1, 2] as const;

/** A canonical-form version that some stored row may legitimately carry. */
export type GuardianAnswerCanonVersion =
  (typeof GUARDIAN_ANSWER_CANON_VERSIONS)[number];

/**
 * The version a NEWLY created invite is hashed and stored under. Bumping this
 * changes only what new rows use — every existing row keeps the version it was
 * written with, and keeps matching.
 */
export const CURRENT_GUARDIAN_ANSWER_CANON_VERSION: GuardianAnswerCanonVersion = 2;

/**
 * The version assumed when a row (or an invite payload from a client bundle
 * predating this change) carries none.
 *
 * MUST be 1 and must never be re-pointed at "whatever is current": a client old
 * enough to omit the field is, by construction, old enough to have hashed under
 * v1. Defaulting to the current version would mislabel exactly the rows this
 * whole mechanism exists to keep matching.
 */
export const LEGACY_GUARDIAN_ANSWER_CANON_VERSION: GuardianAnswerCanonVersion = 1;

/**
 * Narrow an untrusted number to a known version, or null.
 *
 * Fail-closed by design: an unrecognised version (a client claiming v99, a row
 * written by a future deploy and then rolled back) yields null so the caller
 * can refuse rather than silently substitute a form that would not match.
 */
export function asCanonVersion(
  value: unknown,
): GuardianAnswerCanonVersion | null {
  return GUARDIAN_ANSWER_CANON_VERSIONS.includes(value as GuardianAnswerCanonVersion)
    ? (value as GuardianAnswerCanonVersion)
    : null;
}
